import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { finalize, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { STORAGE_CONST } from '@app/constants/storage-constants';
import { LocalStorageUtilLibService } from '@b2b/storage-util-lib';
import { Router } from '@angular/router';
import { EventService } from '@app/services/util-services/root-services/event-service';
import { InitAuthService } from '@app/services/global-services/auth/init-auth-service';
import { HttpClientService } from '@app/api-v2/util/http/http-client.service';

// Global state for token refresh management
let isRefreshing = false;
let refreshTokenSubject: Promise<any> | null = null;

// Configuration constants
const CONFIG = {
  MAX_RETRY_ATTEMPTS: 2,
  RETRY_DELAY: 1000,
  REQUEST_TIMEOUT: 30000,
  AUTH_EXCLUDED_ENDPOINTS: [
    'auth-service/new-login',
    'auth-service/verify-email',
    'auth-service/generate-login-otp-v2',
    'auth-service/generate-registration-otp-v2',
    'auth-service/resend-otp-v2',
    'auth-service/verify-otp',
    'auth-service/refresh-token',
  ],
} as const;

export const WebNoopHttpInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  // Inject services
  const localStorageUtilLibService = inject(LocalStorageUtilLibService);
  const eventService = inject(EventService);
  const initAuthService = inject(InitAuthService);
  const router = inject(Router);
  const b2bDeviceUtilService = inject(B2bDeviceUtilService);
  const httpClientService: HttpClientService = inject(HttpClientService);
  if (b2bDeviceUtilService?.isServer()) {
    return next(req);
  }
  try {
    // Get tokens from storage
    const token = getStorageValue(
      localStorageUtilLibService,
      STORAGE_CONST.AUTH_TOKEN,
    );
    const fbToken = getStorageValue(
      localStorageUtilLibService,
      STORAGE_CONST.FIREBASE_AUTH_TOKEN,
    );

    // Clone request with updated headers
    const newReq = req.clone({
      headers: getUpdatedHeaders(
        req,
        token,
        fbToken,
        router,
        b2bDeviceUtilService,
      ),
    });

    return next(newReq).pipe(
      // Add retry logic for network errors
      /*retry({
        count: shouldRetry(newReq) ? CONFIG.MAX_RETRY_ATTEMPTS : 0,
        delay: (error, retryCount) => {
          if (isNetworkError(error)) {
            return timer(CONFIG.RETRY_DELAY * retryCount);
          }
          return throwError(() => error);
        },
      }),*/
      catchError((error: HttpErrorResponse) =>
        handleHttpError(
          error,
          newReq,
          req,
          next,
          localStorageUtilLibService,
          initAuthService,
          router,
          b2bDeviceUtilService,
          eventService,
          httpClientService,
        ),
      ),
      finalize(() => {
        // Cleanup logic if needed
      }),
    );
  } catch (error) {
    console.error('Error in HTTP interceptor:', error);
    return next(req);
  }
};

/**
 * Handles HTTP errors with improved logic
 */
const handleHttpError = (
  error: HttpErrorResponse,
  newReq: HttpRequest<any>,
  originalReq: HttpRequest<any>,
  next: HttpHandlerFn,
  storageService: LocalStorageUtilLibService,
  authService: InitAuthService,
  router: Router,
  deviceService: B2bDeviceUtilService,
  eventService: EventService,
  httpClientService: HttpClientService,
) => {
  // Handle authentication errors
  if (isAuthError(error)) {
    return handleAuthError(
      error,
      newReq,
      originalReq,
      next,
      storageService,
      authService,
      router,
      deviceService,
      eventService,
      httpClientService,
    );
  }

  // Handle other specific errors
  if (error?.status === 0) {
    console.error('Network error or CORS issue:', error);
  } else if (error?.status >= 500) {
    console.error('Server error:', error);
  }

  return throwError(() => error);
};

/**
 * Handles authentication errors (401, 403)
 */
const handleAuthError = (
  error: HttpErrorResponse,
  newReq: HttpRequest<any>,
  originalReq: HttpRequest<any>,
  next: HttpHandlerFn,
  storageService: LocalStorageUtilLibService,
  authService: InitAuthService,
  router: Router,
  deviceService: B2bDeviceUtilService,
  eventService: EventService,
  httpClientService: HttpClientService,
) => {
  // Skip token refresh for auth endpoints
  if (isAuthEndpoint(newReq.url)) {
    return throwError(() => error);
  }

  // Handle token refresh
  if (!isRefreshing) {
    const refreshToken = getStorageValue(
      storageService,
      STORAGE_CONST.REFRESH_TOKEN,
    );

    if (refreshToken) {
      return handleTokenRefresh(
        refreshToken,
        originalReq,
        next,
        storageService,
        authService,
        router,
        deviceService,
        eventService,
        httpClientService,
      );
    } else {
      // No refresh token available
      handleLogout(authService, eventService);
      return throwError(() => error);
    }
  } else {
    // Token refresh already in progress, wait for it
    return waitForTokenRefresh(originalReq, next, router, deviceService);
  }
};

/**
 * Handles token refresh process
 */
const handleTokenRefresh = (
  refreshToken: string,
  originalReq: HttpRequest<any>,
  next: HttpHandlerFn,
  storageService: LocalStorageUtilLibService,
  authService: InitAuthService,
  router: Router,
  deviceService: B2bDeviceUtilService,
  eventService: EventService,
  httpClientService: HttpClientService,
) => {
  isRefreshing = true;

  const token = getStorageValue(storageService, STORAGE_CONST.AUTH_TOKEN);

  refreshTokenSubject = createRefreshToken(
    refreshToken,
    token,
    httpClientService,
    authService,
  ).toPromise();

  return refreshTokenSubject
    .then(
      (result: {
        token: string;
        refreshToken: string;
        accessToken: string;
      }) => {
        isRefreshing = false;
        refreshTokenSubject = null;

        // Get updated tokens
        const fbToken = getStorageValue(
          storageService,
          STORAGE_CONST.FIREBASE_AUTH_TOKEN,
        );

        // Retry original request with new token
        const retryReq = originalReq.clone({
          headers: getUpdatedHeaders(
            originalReq,
            result?.token || result?.accessToken,
            fbToken,
            router,
            deviceService,
          ),
        });

        return next(retryReq).toPromise();
      },
    )
    .catch((err: HttpErrorResponse) => {
      isRefreshing = false;
      refreshTokenSubject = null;

      console.error('Token refresh failed:', err);
      handleLogout(authService, eventService);

      throw err;
    });
};

/**
 * Waits for ongoing token refresh to complete
 */
const waitForTokenRefresh = (
  originalReq: HttpRequest<any>,
  next: HttpHandlerFn,
  router: Router,
  deviceService: B2bDeviceUtilService,
) => {
  return (
    refreshTokenSubject?.then(() => {
      // Get fresh tokens after refresh
      const storageService = inject(LocalStorageUtilLibService);
      const token = getStorageValue(storageService, STORAGE_CONST.AUTH_TOKEN);
      const fbToken = getStorageValue(
        storageService,
        STORAGE_CONST.FIREBASE_AUTH_TOKEN,
      );

      const retryReq = originalReq.clone({
        headers: getUpdatedHeaders(
          originalReq,
          token,
          fbToken,
          router,
          deviceService,
        ),
      });

      return next(retryReq).toPromise();
    }) || throwError(() => new Error('Token refresh failed'))
  );
};

/**
 * Enhanced header generation with better error handling
 */
const getUpdatedHeaders = (
  req: HttpRequest<any>,
  authToken: string,
  firebaseToken: string,
  router: Router,
  deviceService: B2bDeviceUtilService,
): HttpHeaders => {
  try {
    const urlPath = getCurrentUrlPath(router);
    const timezone = getTimezone();
    const deviceInfo = getDeviceInfo(deviceService);

    return req.headers
      .set('Authorization', `${sanitizeHeaderValue(authToken)}`)
      .set('x-access-token', sanitizeHeaderValue(firebaseToken))
      .set('x-platform', sanitizeHeaderValue(deviceInfo.platform))
      .set('x-os', sanitizeHeaderValue(deviceInfo.os))
      .set('x-manufacturer', sanitizeHeaderValue(deviceInfo.vendor))
      .set('x-app-version', environment?.appVersion + '' || '')
      .set('x-app-version-code', environment?.appVersionCode + '' || '')
      .set('x-i-app-version', environment?.iosAppVersion + '' || '')
      .set('x-i-app-version-code', environment?.iosAppVersionCode + '' || '')
      .set('x-timezone', timezone)
      .set('x-device-width', deviceInfo?.width?.toString())
      .set('x-device-height', deviceInfo?.height?.toString())
      .set('x-app-type', 'web')
      .set('x-app-page', urlPath)
      .set('x-req', 'c')
      .set('x-env', environment?.env);
    /*.set(
        'Content-Type',
        req.headers.get('Content-Type') || 'application/json',
      )
      .set('Accept', 'application/json')*/
  } catch (error) {
    console.error('Error generating headers:', error);
    return req.headers;
  }
};

/**
 * Utility functions
 */
const getStorageValue = (
  storageService: LocalStorageUtilLibService,
  key: string,
): string => {
  try {
    return storageService.get(key) || '';
  } catch (error) {
    console.error(`Error getting storage value for key ${key}:`, error);
    return '';
  }
};

const sanitizeHeaderValue = (value: string | null | undefined): string => {
  if (
    !value ||
    value?.trim() === '' ||
    value === 'null' ||
    value === 'undefined'
  ) {
    return '';
  }
  return value.toString().trim();
};

const getCurrentUrlPath = (router: Router): string => {
  try {
    let urlPath = router?.routerState?.snapshot?.url || '';
    if (urlPath?.includes('?')) {
      urlPath = urlPath?.substring(0, urlPath?.indexOf('?'));
    }
    return urlPath;
  } catch (error) {
    console.error('Error getting current URL path:', error);
    return '';
  }
};

const getTimezone = (): string => {
  try {
    return Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
  } catch (error) {
    console.error('Error getting timezone:', error);
    return 'UTC';
  }
};

const getDeviceInfo = (deviceService: B2bDeviceUtilService) => {
  try {
    return {
      platform: deviceService.getWebPlatform() || 'unknown',
      os: deviceService.getWebOS() || 'unknown',
      vendor: deviceService.getWebVendor() || 'unknown',
      width: deviceService.getWidth() || 0,
      height: deviceService.getHeight() || 0,
    };
  } catch (error) {
    console.error('Error getting device info:', error);
    return {
      platform: 'unknown',
      os: 'unknown',
      vendor: 'unknown',
      width: 0,
      height: 0,
    };
  }
};

const isAuthError = (error: HttpErrorResponse): boolean => {
  return error?.status === 401 || error?.status === 403;
};

const isNetworkError = (error: HttpErrorResponse): boolean => {
  return error?.status === 0 || error?.status === 408 || error?.status === 429;
};

const isAuthEndpoint = (url: string): boolean => {
  return CONFIG.AUTH_EXCLUDED_ENDPOINTS.some((endpoint) =>
    url?.includes(endpoint),
  );
};

const shouldRetry = (req: HttpRequest<any>): boolean => {
  // Only retry GET requests and non-auth endpoints
  return req?.method === 'GET' && !isAuthEndpoint(req?.url);
};

const handleLogout = (
  authService: InitAuthService,
  eventService: EventService,
): void => {
  try {
    eventService.loggedOutEvent.emit();
    authService.removeRefreshToken().catch((error) => {
      console.error('Error removing refresh token:', error);
    });
    authService.logout();
  } catch (error) {
    console.error('Error during logout:', error);
  }
};

const createRefreshToken = (
  refreshToken: string,
  authToken: string,
  httpClientService: HttpClientService,
  initAuthService: InitAuthService,
): Observable<{
  token: string;
  refreshToken: string;
}> => {
  const options = {
    headers: new HttpHeaders()
      .set('x-refresh-token', `${refreshToken}`)
      .append('authorization', authToken),
  };

  return httpClientService.post('auth-service/refresh-token', {}, options).pipe(
    map(
      (result: {
        token: string;
        refreshToken: string;
        accessToken: string;
      }) => {
        initAuthService.authToken = result?.token;
        initAuthService.refreshToken = result?.refreshToken;
        initAuthService.setRefreshToken(result?.refreshToken);
        initAuthService.setB2BToken(result?.token).then(() => {
          initAuthService?.handleRedirectAfterLogin();
        });
        return result;
      },
    ),
  );
};
